import { Link, Outlet, useNavigate, useRouterState } from '@tanstack/react-router';
import {
  cn,
  Dialog,
  Kbd,
  Menu,
  MenuContent,
  MenuItem,
  MenuLabel,
  MenuSeparator,
  MenuTrigger,
  Spinner,
} from '@tatra/ui';
import { useQuery } from '@tanstack/react-query';
import {
  CalendarDays,
  Check,
  ChevronLeft,
  KeyRound,
  LogOut,
  Menu as MenuIcon,
  Monitor,
  Moon,
  Pin,
  Search,
  Sun,
  UserPlus,
} from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { CommandPalette, type PaletteAction } from '../components/CommandPalette.js';
import { QuickAddButton } from '../components/QuickAdd.js';
import { DispositionPrompt } from '../features/comms/DispositionPrompt.js';
import { ExtensionsProvider, useExtensions } from '../extensions/runtime.js';
import { MfaEnrolmentRequiredScreen, NetworkBlockedScreen } from '../features/auth/AccessBlockedScreens.js';
import { NotificationCentre } from '../features/notifications/NotificationCentre.js';
import { OfflineBar, OfflineScreen } from '../features/pwa/Offline.js';
import { FirstRunTour } from '../features/tours/FirstRunTour.js';
import { get } from '../lib/api.js';
import { quickAdd } from '../lib/quick-add.js';
import { useAuth } from '../lib/auth.js';
import { AREAS, matchNav, MODULE_ICON, type NavItem, SETTINGS_GROUPS, visibleNav } from '../lib/nav.js';
import { connectRealtime, disconnectRealtime } from '../lib/realtime.js';
import { applyTheme, getTheme, type ThemeChoice } from '../lib/theme.js';

const SHORTCUTS: [string, string][] = [
  ['Ctrl / ⌘ K', 'Search and commands'],
  ['/', 'Search'],
  ['g then t', 'Today'],
  ['g then l', 'Leads'],
  ['g then k', 'Pipeline'],
  ['g then v', 'Site visits'],
  ['g then w', 'Walk-in'],
  ['g then i', 'Inventory'],
  ['g then p', 'Projects'],
  ['g then m', 'Members'],
  ['g then b', 'WhatsApp inbox'],
  ['g then c', 'Calls'],
  ['?', 'This list'],
  ['Esc', 'Close a dialog or drawer'],
];

function useGlobalShortcuts(openPalette: () => void, openHelp: () => void, nav: NavItem[]) {
  const navigate = useNavigate();
  const pendingG = useRef<number | null>(null);
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        openPalette();
        return;
      }
      const target = e.target as HTMLElement | null;
      const typing =
        target && (target.isContentEditable || ['INPUT', 'TEXTAREA', 'SELECT'].includes(target.tagName));
      if (typing || e.ctrlKey || e.metaKey || e.altKey) return;
      if (e.key === '/' || e.key === '?') {
        e.preventDefault();
        if (e.key === '/') openPalette();
        else openHelp();
        return;
      }
      if (pendingG.current !== null) {
        const item = nav.find((n) => n.shortcut === e.key.toLowerCase());
        window.clearTimeout(pendingG.current);
        pendingG.current = null;
        if (item) {
          e.preventDefault();
          void navigate({ to: item.to });
        }
        return;
      }
      if (e.key === 'g') pendingG.current = window.setTimeout(() => (pendingG.current = null), 1200);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [navigate, openHelp, openPalette, nav]);
}

interface AreaEntry {
  key: string;
  label: string;
  icon: NavItem['icon'];
  to: string;
  zone: 'sell' | 'run' | 'admin';
  items: NavItem[];
}

/**
 * One sidebar entry per area (D-123): the area opens its first page, and its pages show as tabs on top.
 * Settings opens the Settings page, which lists every settings page by group. Extension-module items
 * without an area keep their own entry.
 */
function areaEntries(items: NavItem[]): AreaEntry[] {
  const out: AreaEntry[] = [];
  for (const a of AREAS) {
    const own = items.filter((i) => i.area === a.key);
    const shown = own.filter((i) => !i.hidden);
    if (shown.length === 0) continue;
    out.push({
      key: a.key,
      label: shown.length === 1 && a.key !== 'settings' ? shown[0]!.label : a.label,
      icon: a.icon,
      to: a.key === 'settings' ? '/admin' : shown[0]!.to,
      zone: a.zone,
      items: own,
    });
  }
  for (const i of items.filter((i) => !i.area && !i.hidden)) {
    out.push({
      key: i.to,
      label: i.label,
      icon: i.icon,
      to: i.to,
      zone: i.section === 'admin' ? 'admin' : 'run',
      items: [i],
    });
  }
  return out;
}

function useCurrentArea(items: NavItem[]): string | undefined {
  const path = useRouterState({ select: (s) => s.location.pathname });
  if (path === '/admin') return 'settings';
  const hit = matchNav(items, path);
  return hit ? (hit.area ?? hit.to) : undefined;
}

const railLink =
  'group flex min-h-11 items-center gap-3 rounded-lg px-3 text-sm transition-colors duration-150 md:min-h-9';
const railIdle = 'text-nav-muted hover:bg-nav-hover hover:text-nav-text';
const railActive = 'bg-nav-active-bg font-medium text-nav-text';

function AreaLink({ area, active, onClick }: { area: AreaEntry; active: boolean; onClick?: () => void }) {
  return (
    <Link
      to={area.to}
      onClick={onClick}
      aria-current={active ? 'page' : undefined}
      className={cn(railLink, active ? railActive : railIdle)}
    >
      <area.icon
        className={cn(
          'h-[18px] w-[18px] shrink-0',
          active ? 'text-nav-active' : 'text-nav-faint group-hover:text-nav-muted',
        )}
        aria-hidden="true"
      />
      <span className="truncate">{area.label}</span>
    </Link>
  );
}

interface PinnedView {
  id: string;
  name: string;
  screen: 'LEADS' | 'PIPELINE';
}

/** Saved views the member pinned (UX §2 "Pinned"): each opens its list or board with the filters. */
function PinnedViews({ onNavigate }: { onNavigate?: () => void }) {
  const { can } = useAuth();
  const search = useRouterState({ select: (s) => s.location.search as Record<string, unknown> });
  const pinned = useQuery({
    queryKey: ['saved-views', 'pinned=true'],
    queryFn: () => get<PinnedView[]>('/saved-views?pinned=true'),
    enabled: can('leads.lead.read'),
    staleTime: 30_000,
  });
  if (!pinned.data?.length) return null;
  return (
    <div data-testid="pinned-views">
      <RailHeading>Pinned</RailHeading>
      {pinned.data.map((v) => {
        const active = search['view'] === v.id;
        return (
          <Link
            key={v.id}
            to="/leads"
            search={(v.screen === 'LEADS' ? { view: v.id } : { tab: 'board', view: v.id }) as never}
            onClick={onNavigate}
            aria-current={active ? 'page' : undefined}
            className={cn(railLink, active ? railActive : railIdle)}
          >
            <Pin className="h-4 w-4 shrink-0 text-nav-faint" aria-hidden="true" />
            <span className="truncate">{v.name}</span>
          </Link>
        );
      })}
    </div>
  );
}

function RailHeading({ children }: { children: string }) {
  return (
    <p className="mt-5 mb-1.5 px-3 text-[11px] font-medium tracking-[0.08em] text-nav-faint uppercase">
      {children}
    </p>
  );
}

/** The organisation's name as a wordmark, with the brand's magenta point. */
function Wordmark({ name }: { name: string }) {
  return (
    <span className="flex min-w-0 items-center gap-2.5">
      <span
        aria-hidden="true"
        className="h-2.5 w-2.5 shrink-0 rounded-full bg-[linear-gradient(135deg,#ff6fc9,#ec1f9c_45%,#a855f7)] shadow-[0_0_0_3px_rgb(236_31_156/0.18)]"
      />
      <span className="truncate font-display text-[17px] font-semibold tracking-[-0.01em]">{name}</span>
    </span>
  );
}

function Sidebar({ items, onNavigate }: { items: NavItem[]; onNavigate?: () => void }) {
  const current = useCurrentArea(items);
  const areas = areaEntries(items);
  const zone = (z: AreaEntry['zone']) =>
    areas
      .filter((a) => a.zone === z)
      .map((a) => <AreaLink key={a.key} area={a} active={current === a.key} onClick={onNavigate} />);
  const run = zone('run');
  const admin = zone('admin');
  return (
    <nav aria-label="Main" className="flex flex-1 flex-col overflow-y-auto px-3 pt-2 pb-4">
      <div className="space-y-0.5">{zone('sell')}</div>
      {run.length > 0 && (
        <>
          <RailHeading>Business</RailHeading>
          <div className="space-y-0.5">{run}</div>
        </>
      )}
      <PinnedViews onNavigate={onNavigate} />
      {admin.length > 0 && <div className="mt-auto space-y-0.5 pt-5">{admin}</div>}
    </nav>
  );
}

/**
 * The pages of the current area as tabs (D-123): Projects · Inventory · Site plans …, so a member moves
 * within a job without going back to the menu. Shown on the area's own pages, not on a record's page.
 */
function AreaTabs({ items }: { items: NavItem[] }) {
  const path = useRouterState({ select: (s) => s.location.pathname });
  const here = items.find((i) => i.to === path);
  if (!here?.area) return null;
  const siblings = items.filter(
    (i) => i.area === here.area && !i.hidden && (here.area !== 'settings' || i.group === here.group),
  );
  if (here.area !== 'settings' && siblings.length < 2) return null;
  const group = SETTINGS_GROUPS.find((g) => g.key === here.group);
  return (
    <nav
      aria-label="Section"
      className="-mx-4 mb-5 flex items-center gap-1 overflow-x-auto border-b border-border px-4 sm:-mx-6 sm:px-6"
    >
      {here.area === 'settings' && (
        <Link
          to="/admin"
          className="-mb-px flex min-h-11 shrink-0 items-center gap-1.5 border-b-2 border-transparent pr-3 text-sm text-text-muted hover:text-text md:min-h-10"
        >
          <ChevronLeft className="h-4 w-4" aria-hidden="true" />
          {group ? group.label : 'Settings'}
        </Link>
      )}
      {siblings.map((i) => {
        const active = i.to === path;
        return (
          <Link
            key={i.to}
            to={i.to}
            aria-current={active ? 'page' : undefined}
            className={cn(
              '-mb-px flex min-h-11 shrink-0 items-center border-b-2 px-3 text-sm font-medium whitespace-nowrap transition-colors md:min-h-10',
              active
                ? 'border-primary text-text'
                : 'border-transparent text-text-muted hover:border-border-strong hover:text-text',
            )}
          >
            {i.tab ?? i.label}
          </Link>
        );
      })}
    </nav>
  );
}

/**
 * The phone's bottom tab bar (M15, D-140): Today, Leads, Inventory and Visits — each only when the member
 * may open it — then More (every other page). Visits is the Leads page's calendar tab. A role without
 * some of them gets its own most-used pages (`mobile` rank in nav.ts) in the free places.
 */
function PhoneTabBar({ items, onMore }: { items: NavItem[]; onMore: () => void }) {
  const { can } = useAuth();
  const loc = useRouterState({
    select: (s) => ({ path: s.location.pathname, search: s.location.search as Record<string, unknown> }),
  });
  const onVisits = loc.path === '/leads' && loc.search['tab'] === 'visits';
  const byPath = (to: string) => items.find((i) => i.to === to);
  const tabs: {
    key: string;
    label: string;
    icon: NavItem['icon'];
    to: string;
    search?: Record<string, string>;
    active: boolean;
  }[] = [];
  const today = byPath('/');
  if (today) tabs.push({ key: 'today', label: 'Today', icon: today.icon, to: '/', active: loc.path === '/' });
  const leads = byPath('/leads');
  if (leads)
    tabs.push({
      key: 'leads',
      label: 'Leads',
      icon: leads.icon,
      to: '/leads',
      active: loc.path.startsWith('/leads') && !onVisits,
    });
  const inventory = byPath('/inventory');
  if (inventory)
    tabs.push({
      key: 'inventory',
      label: 'Inventory',
      icon: inventory.icon,
      to: '/inventory',
      active: loc.path.startsWith('/inventory'),
    });
  if (leads && can('activity.visit.read'))
    tabs.push({
      key: 'visits',
      label: 'Visits',
      icon: CalendarDays,
      to: '/leads',
      search: { tab: 'visits' },
      active: onVisits,
    });
  // A role without some of those gets its own most-used pages in the free places.
  for (const i of items
    .filter((x) => x.mobile !== undefined)
    .sort((a, b) => (a.mobile ?? 99) - (b.mobile ?? 99))) {
    if (tabs.length >= 4) break;
    if (tabs.some((t) => t.to === i.to)) continue;
    tabs.push({
      key: i.to,
      label: i.to === '/inbox' ? 'Inbox' : i.label,
      icon: i.icon,
      to: i.to,
      active: loc.path.startsWith(i.to),
    });
  }
  return (
    <nav
      aria-label="Quick"
      data-testid="phone-tabs"
      className="fixed inset-x-0 bottom-0 z-30 flex border-t border-nav-border bg-nav-bg/95 px-1 pb-[env(safe-area-inset-bottom)] backdrop-blur-md lg:hidden"
    >
      {tabs.map((t) => (
        <Link
          key={t.key}
          to={t.to}
          {...(t.search ? { search: t.search as never } : {})}
          aria-current={t.active ? 'page' : undefined}
          activeOptions={{ exact: true, includeSearch: true }}
          className={cn(
            'group flex min-h-16 flex-1 flex-col items-center justify-center gap-1 text-[11px] font-medium',
            t.active ? 'text-nav-text' : 'text-nav-muted',
          )}
        >
          <span
            className={cn(
              'flex h-7 w-12 items-center justify-center rounded-full transition-colors',
              t.active && 'bg-nav-active-bg',
            )}
          >
            <t.icon className={cn('h-5 w-5', t.active && 'text-nav-active')} aria-hidden="true" />
          </span>
          {t.label}
        </Link>
      ))}
      <button
        type="button"
        onClick={onMore}
        className="flex min-h-16 flex-1 flex-col items-center justify-center gap-1 text-[11px] font-medium text-nav-muted"
      >
        <span className="flex h-7 w-12 items-center justify-center">
          <MenuIcon className="h-5 w-5" aria-hidden="true" />
        </span>
        More
      </button>
    </nav>
  );
}

/** The phone "More" sheet: every area with its pages, one tap each. */
function MoreSheet({ items, onNavigate }: { items: NavItem[]; onNavigate: () => void }) {
  const areas = areaEntries(items);
  const current = useCurrentArea(items);
  return (
    <nav aria-label="Main" className="space-y-4">
      {areas.map((a) => {
        const pages =
          a.key === 'settings'
            ? [{ to: '/admin', label: 'All settings' }]
            : a.items.filter((i) => !i.hidden).map((i) => ({ to: i.to, label: i.tab ?? i.label }));
        return (
          <section key={a.key}>
            <Link
              to={a.to}
              onClick={onNavigate}
              aria-current={current === a.key ? 'page' : undefined}
              className={cn(
                'flex min-h-11 items-center gap-3 rounded-lg px-2 text-sm font-medium',
                current === a.key ? 'text-primary' : 'text-text',
              )}
            >
              <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary-soft text-primary">
                <a.icon className="h-4 w-4" aria-hidden="true" />
              </span>
              {a.label}
            </Link>
            {pages.length > 1 && (
              <div className="mt-1 flex flex-wrap gap-1.5 pl-12">
                {pages.map((p) => (
                  <Link
                    key={p.to}
                    to={p.to}
                    onClick={onNavigate}
                    className="inline-flex min-h-10 items-center rounded-full border border-border px-3 text-sm text-text-muted hover:border-border-strong hover:text-text"
                  >
                    {p.label}
                  </Link>
                ))}
              </div>
            )}
          </section>
        );
      })}
      <PinnedViewsSheet onNavigate={onNavigate} />
    </nav>
  );
}

/** Pinned views in the phone sheet (the same list as the sidebar, in the sheet's colours). */
function PinnedViewsSheet({ onNavigate }: { onNavigate: () => void }) {
  const { can } = useAuth();
  const pinned = useQuery({
    queryKey: ['saved-views', 'pinned=true'],
    queryFn: () => get<PinnedView[]>('/saved-views?pinned=true'),
    enabled: can('leads.lead.read'),
    staleTime: 30_000,
  });
  if (!pinned.data?.length) return null;
  return (
    <section data-testid="pinned-views">
      <p className="mb-1 px-2 text-xs font-medium tracking-[0.06em] text-text-faint uppercase">Pinned</p>
      {pinned.data.map((v) => (
        <Link
          key={v.id}
          to="/leads"
          search={(v.screen === 'LEADS' ? { view: v.id } : { tab: 'board', view: v.id }) as never}
          onClick={onNavigate}
          className="flex min-h-11 items-center gap-3 rounded-lg px-2 text-sm text-text hover:bg-surface-hover"
        >
          <Pin className="h-4 w-4 shrink-0 text-text-faint" aria-hidden="true" />
          <span className="truncate">{v.name}</span>
        </Link>
      ))}
    </section>
  );
}

export function AppShell() {
  return (
    <ExtensionsProvider>
      <Shell />
    </ExtensionsProvider>
  );
}

function Shell() {
  const { status, me, can, signOut } = useAuth();
  const onSetup = useRouterState({ select: (st) => st.location.pathname === '/setup' });
  const { modules } = useExtensions();
  const navigate = useNavigate();
  const [palette, setPalette] = useState(false);
  const [help, setHelp] = useState(false);
  const [mobileNav, setMobileNav] = useState(false);
  const [theme, setTheme] = useState<ThemeChoice>(getTheme);
  const moduleNav: NavItem[] = modules.flatMap((m) =>
    (m.web.nav ?? []).map((n) => ({
      to: n.to,
      label: n.label,
      icon: n.icon ?? MODULE_ICON,
      anyOf: n.permission ? [n.permission] : [],
      section: n.section ?? 'main',
    })),
  );
  const items = visibleNav(can, moduleNav);
  const moduleCommands: PaletteAction[] = modules.flatMap((m) =>
    (m.web.commands ?? [])
      .filter((c) => !c.permission || can(c.permission))
      .map((c) => ({
        id: `${m.info.key}:${c.id}`,
        label: c.label,
        keywords: c.keywords?.join(' '),
        run: () => c.run(m.ctx),
      })),
  );
  // Quick-add in the palette too (UX §1: "New lead", "Book site visit …").
  const quickCommands: PaletteAction[] = [
    ...(can('leads.lead.create')
      ? [
          { id: 'quick:lead', label: 'New lead', keywords: 'add create', run: () => quickAdd.open('lead') },
          {
            id: 'quick:walk-in',
            label: 'New walk-in',
            keywords: 'visitor site office',
            run: () => void navigate({ to: '/walk-in' }),
          },
        ]
      : []),
    ...(can('activity.task.manage')
      ? [
          {
            id: 'quick:task',
            label: 'New task',
            keywords: 'follow up call reminder',
            run: () => quickAdd.open('task'),
          },
        ]
      : []),
    ...(can('activity.visit.manage')
      ? [
          {
            id: 'quick:visit',
            label: 'Book a site visit',
            keywords: 'visit slot',
            run: () => quickAdd.open('visit'),
          },
        ]
      : []),
    ...(can('leads.lead.update')
      ? [
          {
            id: 'quick:note',
            label: 'Add a note to a lead',
            keywords: 'note log',
            run: () => quickAdd.open('note'),
          },
        ]
      : []),
    ...(can('leads.lead.read')
      ? [
          {
            id: 'go:drop-off',
            label: 'Stage drop-off report',
            keywords: 'funnel report pipeline',
            run: () => void navigate({ to: '/pipeline/drop-off' }),
          },
        ]
      : []),
  ];
  useGlobalShortcuts(
    () => setPalette(true),
    () => setHelp(true),
    items,
  );

  useEffect(() => {
    if (status !== 'signed_in') return;
    connectRealtime();
    return () => disconnectRealtime();
  }, [status]);

  useEffect(() => {
    if (status === 'signed_out')
      void navigate({ to: '/login', search: { redirect: window.location.pathname } });
  }, [status, navigate]);

  if (status === 'mfa_enrolment') return <MfaEnrolmentRequiredScreen />;
  if (status === 'network_blocked') return <NetworkBlockedScreen />;
  if (status === 'offline') return <OfflineScreen />;
  if (status !== 'signed_in' || !me) {
    return (
      <div className="flex min-h-dvh items-center justify-center">
        <Spinner />
      </div>
    );
  }

  const chooseTheme = (t: ThemeChoice) => {
    setTheme(t);
    applyTheme(t);
  };
  const initials = me.member.displayName
    .split(/\s+/)
    .map((w) => w[0])
    .slice(0, 2)
    .join('')
    .toUpperCase();

  return (
    <div className="flex min-h-dvh bg-bg">
      <a
        href="#main"
        className="sr-only focus:not-sr-only focus:fixed focus:top-3 focus:left-3 focus:z-50 focus:rounded focus:bg-primary focus:px-3 focus:py-2 focus:text-primary-fg"
      >
        Skip to content
      </a>

      <aside className="sticky top-0 hidden h-dvh w-60 shrink-0 flex-col bg-nav-bg text-nav-text lg:flex">
        <div className="flex h-16 items-center px-5">
          <Link to="/" className="min-w-0 rounded-md">
            <Wordmark name={me.organisation.brandName} />
          </Link>
        </div>
        {can('leads.lead.create') && (
          <div className="px-3 pb-2">
            <button
              type="button"
              onClick={() => quickAdd.open('lead')}
              className="flex min-h-10 w-full items-center gap-2 rounded-lg bg-primary px-3 text-sm font-medium text-primary-fg shadow-[0_6px_20px_-8px_rgb(236_31_156/0.7)] transition-colors hover:bg-primary-hover"
            >
              <UserPlus className="h-4 w-4" aria-hidden="true" />
              New lead
            </button>
          </div>
        )}
        <Sidebar items={items} />
        <div className="border-t border-nav-border px-3 py-3">
          <Link
            to="/profile"
            className="flex min-h-11 items-center gap-3 rounded-lg px-2 text-sm text-nav-muted transition-colors hover:bg-nav-hover hover:text-nav-text"
          >
            <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-nav-surface text-xs font-semibold text-nav-active">
              {initials}
            </span>
            <span className="min-w-0 leading-tight">
              <span className="block truncate font-medium text-nav-text">{me.member.displayName}</span>
              <span className="block truncate text-xs text-nav-faint">Profile and security</span>
            </span>
          </Link>
        </div>
      </aside>

      <Dialog open={mobileNav} onOpenChange={setMobileNav} title="Everything">
        <MoreSheet items={items} onNavigate={() => setMobileNav(false)} />
      </Dialog>

      <div className="flex min-w-0 flex-1 flex-col">
        <header className="sticky top-0 z-30 flex h-16 items-center gap-2 border-b border-border bg-bg/85 px-3 backdrop-blur-md sm:px-6">
          <Link to="/" className="mr-1 shrink-0 rounded-md text-text lg:hidden" aria-label="Today">
            <span className="hidden sm:inline">
              <Wordmark name={me.organisation.brandName} />
            </span>
            <span
              aria-hidden="true"
              className="block h-3 w-3 rounded-full bg-[linear-gradient(135deg,#ff6fc9,#ec1f9c_45%,#a855f7)] sm:hidden"
            />
          </Link>
          <button
            type="button"
            onClick={() => setPalette(true)}
            className="flex min-h-10 flex-1 items-center gap-2 rounded-lg border border-border bg-surface px-3 text-left text-sm text-text-faint shadow-soft transition-colors hover:border-border-strong sm:max-w-md"
          >
            <Search className="h-4 w-4" aria-hidden="true" />
            <span className="flex-1 truncate">Search leads, units, people…</span>
            <span className="hidden sm:inline">
              <Kbd>Ctrl K</Kbd>
            </span>
          </button>
          <div className="ml-auto flex items-center gap-1">
            <NotificationCentre />
            <Menu>
              <MenuTrigger asChild>
                <button
                  type="button"
                  aria-label="Account menu"
                  className="flex h-10 w-10 items-center justify-center rounded-full bg-primary-soft text-sm font-semibold text-primary transition-colors hover:bg-primary hover:text-primary-fg"
                >
                  {initials}
                </button>
              </MenuTrigger>
              <MenuContent>
                <MenuLabel>
                  {me.member.displayName}
                  <br />
                  <span className="text-text-faint">{me.role.name}</span>
                </MenuLabel>
                <MenuSeparator />
                <MenuItem onSelect={() => void navigate({ to: '/profile' })}>
                  <KeyRound className="h-4 w-4" /> Profile & security
                </MenuItem>
                <MenuItem onSelect={() => chooseTheme('light')}>
                  <Sun className="h-4 w-4" /> <span className="flex-1">Light</span>{' '}
                  {theme === 'light' && <Check className="h-4 w-4 text-primary" />}
                </MenuItem>
                <MenuItem onSelect={() => chooseTheme('dark')}>
                  <Moon className="h-4 w-4" /> <span className="flex-1">Dark</span>{' '}
                  {theme === 'dark' && <Check className="h-4 w-4 text-primary" />}
                </MenuItem>
                <MenuItem onSelect={() => chooseTheme('system')}>
                  <Monitor className="h-4 w-4" /> <span className="flex-1">Match device</span>{' '}
                  {theme === 'system' && <Check className="h-4 w-4 text-primary" />}
                </MenuItem>
                <MenuItem onSelect={() => setHelp(true)}>Keyboard shortcuts</MenuItem>
                <MenuSeparator />
                <MenuItem onSelect={() => void signOut()} className="text-danger">
                  <LogOut className="h-4 w-4" /> Sign out
                </MenuItem>
              </MenuContent>
            </Menu>
          </div>
        </header>

        <main id="main" className="mx-auto w-full max-w-7xl flex-1 px-4 pt-5 pb-28 sm:px-6 sm:pt-7 lg:pb-10">
          {me.setup && !me.setup.completed && !onSetup && (
            <div
              role="status"
              data-testid="setup-banner"
              className="mb-5 flex flex-wrap items-center gap-3 rounded-2xl border border-primary/25 bg-primary-soft px-4 py-3 text-sm"
            >
              <span className="min-w-0 flex-1 text-text">
                <span className="font-medium">Finish setting up {me.organisation.brandName}.</span>{' '}
                <span className="text-text-muted">
                  {me.setup.done} of {me.setup.total} steps done.
                </span>
              </span>
              <Link
                to="/setup"
                className="inline-flex min-h-11 items-center rounded-lg bg-primary px-3.5 font-medium text-primary-fg hover:bg-primary-hover md:min-h-9"
              >
                Continue set-up
              </Link>
            </div>
          )}
          <OfflineBar memberId={me.member.id} />
          <AreaTabs items={items} />
          <Outlet />
        </main>

        <PhoneTabBar items={items} onMore={() => setMobileNav(true)} />
      </div>

      <QuickAddButton />
      <FirstRunTour />
      <DispositionPrompt />
      <CommandPalette
        open={palette}
        onOpenChange={setPalette}
        extraActions={[...quickCommands, ...moduleCommands]}
      />
      <Dialog open={help} onOpenChange={setHelp} title="Keyboard shortcuts" size="sm">
        <dl className="space-y-2 text-sm">
          {SHORTCUTS.map(([k, v]) => (
            <div key={k} className="flex items-center justify-between gap-3">
              <dt className="text-text-muted">{v}</dt>
              <dd>
                <Kbd>{k}</Kbd>
              </dd>
            </div>
          ))}
        </dl>
      </Dialog>
    </div>
  );
}
